import {AddItemCell, AddItemContextState} from '../types';
import {AddItemContext, initialState} from '../context/AddItem';
import {
  CHECK_FOR_FROZEN_ITEMS,
  SEARCH_CATALOG,
} from '../../config/graphQL/documentNode/queries';
import {Dispatch, useContext, useReducer, useState} from 'react';
import {PayloadAction, createSlice} from '@reduxjs/toolkit';
import {
  SearchItemsForSubParams,
  SearchResults,
} from '../../itemDetails/types/itemReplacement';

import {ADD_ITEM} from '../../config/graphQL/documentNode/mutation';
import {AddItemParams} from '../../itemDetails/types/CustomItem';
import {Analytics} from '@buncha/config/analytics/analytics';
import {SearchCatalogResponse} from '../../config/graphQL/type';
import {ToastType} from '../../components/composites/notification/type';
import {getClientType} from '@buncha/common/types/common';
import {gqlService} from '../../config/graphQL';
import {useCheckIfB2BStore} from '@buncha/utils/common';
import {useToastMessage} from '../../components/composites/notification';

const reducer = {
  setSelectedItem: function (
    state: AddItemContextState,
    action: PayloadAction<AddItemCell | undefined>,
  ) {
    state.selectedItem = action.payload;
    state.selectedQuantity = 1;
  },
  setSelectedQuantity: function (
    state: AddItemContextState,
    action: PayloadAction<number>,
  ) {
    state.selectedQuantity = action.payload;
  },
  setSearchResults: function (
    state: AddItemContextState,
    action: PayloadAction<AddItemCell[]>,
  ) {
    state.searchResults = action.payload;
  },
};

const slice = createSlice({
  initialState,
  name: 'addItem',
  reducers: reducer,
});

export function useAddItemReducer() {
  const addItemReducer = useReducer(slice.reducer, initialState);
  return addItemReducer;
}

export function useAddItemContext() {
  return useContext(AddItemContext);
}

export const AddItemActions = slice.actions;

export function useSearchItemsForAddItems(
  dispatch: Dispatch<any> | null,
  state: AddItemContextState,
) {
  const [showErrorToast] = useToastMessage(ToastType.Error);
  const [loading, setLoading] = useState(false);
  const [moreItems, setMoreItems] = useState(true);
  const {checkIfB2BStore} = useCheckIfB2BStore();
  const searchItemsForAddition = async (params: SearchItemsForSubParams) => {
    if ((params.from === 0 || !params.searchQuery) && dispatch) {
      dispatch(AddItemActions.setSelectedItem(undefined));
      dispatch(AddItemActions.setSearchResults([]));
      setMoreItems(true);
    }
    if (!params.searchQuery || !params.storeId) return;
    const isB2BStore = checkIfB2BStore(params.storeId);
    try {
      setLoading(true);
      const res = await gqlService?.query<SearchCatalogResponse>({
        query: SEARCH_CATALOG,
        fetchPolicy: 'network-only',
        variables: params,
      });
      const searchCatalog = res?.data?.searchCatalog;
      const searchItems = searchCatalog?.items || [];
      const resultExists = searchItems?.length;
      const totalResult = searchCatalog?.totalResult || 0;
      const searchSource = 'Add Item';
      const eventProps = {
        source: searchSource,
        storeId: params.storeId,
        searchItem: params.searchQuery,
        runId: params.runId,
      };
      if (
        totalResult === 0 ||
        (searchCatalog?.searchRelevance?.[0]?.key === 'other' && !isB2BStore)
      )
        Analytics.eventWithProps('Searched but not found', {
          ...eventProps,
          from: params.from,
          size: params.size,
          cleint: getClientType(isB2BStore),
        });

      if (searchCatalog?.searchRelevance?.length && !isB2BStore && resultExists)
        Analytics.eventWithProps('Searched Result', {
          ...eventProps,
          type: 'CTA',
          cleint: getClientType(isB2BStore),
          top5_searched_name: searchItems
            .map((item: SearchResults) => item.name)
            .slice(0, 5),
        });
      if (resultExists && dispatch)
        dispatch(
          AddItemActions.setSearchResults([
            ...(params.from ? state.searchResults : []),
            ...searchItems,
          ]),
        );
      if (searchItems.length < params.size) setMoreItems(false);

      setLoading(false);
    } catch (err: any) {
      setLoading(false);
      showErrorToast(err?.message);
      // errorService.report(err);
    }
  };

  return {searchItemsForAddition, loading, moreItems};
}

export function useAddItem() {
  const [addItemLoading, setAddItemLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const addItem = async (params: AddItemParams, callback?: Function) => {
    setAddItemLoading(true);
    try {
      setErrorMessage('');
      params.source = 'shopper-addSearchItem';
      const response = await gqlService?.mutation({
        mutation: ADD_ITEM,
        fetchPolicy: 'network-only',
        variables: params,
      });
      const orderItemId = response?.data?.addItemInRun?.id || 0;
      if (orderItemId && callback) callback(orderItemId);
      setAddItemLoading(false);
    } catch (error: any) {
      setAddItemLoading(false);
      setErrorMessage(error?.message);
    }
  };

  return {addItemLoading, addItem, errorMessage};
}

export function useCheckForFrozenItems() {
  const [checkingItemForPick, setCheckingItemForPick] = useState(false);
  const checkForFrozenItems = async (runId?: number, itemIds?: number[]) => {
    if (!runId) return;
    setCheckingItemForPick(true);
    try {
      const response = await gqlService?.mutation({
        mutation: CHECK_FOR_FROZEN_ITEMS,
        variables: {runId: runId, itemIds: itemIds},
        fetchPolicy: 'network-only',
      });
      if (response?.data) {
        const allowToPick = response?.data?.checkForFrozenItems;
        setCheckingItemForPick(false);
        return allowToPick;
      }
      setCheckingItemForPick(false);
      return false;
    } catch (err: any) {
      setCheckingItemForPick(false);
      return false;
    }
  };

  return {checkingItemForPick, checkForFrozenItems};
}
